import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Camera, X, RefreshCw, Check, AlertTriangle, ScanLine, Sparkles, FilePlus, Trash2, Crop } from "lucide-react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { axiosInstance } from "../App";
import { ocrReceipt } from "../lib/receiptOcr";

/**
 * ExpenseCameraCapture (v3 — Document-scanner pipeline + multi-page)
 * ─────────────────────────────────────────────────────────────────
 * 1. Opens the rear camera at the highest resolution the device exposes,
 *    with continuous focus (when supported) so the receipt stays sharp.
 * 2. After capture, runs an edge-detection pass to find the actual
 *    rectangle of the receipt (light region against a darker background)
 *    and crops to it — so the output matches the *shape* of the receipt,
 *    not a fixed viewfinder rectangle.
 * 3. Applies a document-scanner enhancement (contrast stretch + grayscale
 *    + light unsharp-mask) for crisp, legible text.
 * 4. Multi-page: the user can stack as many pages as they want; on "حفظ"
 *    every page is composited into a single multi-page PDF (jsPDF) and
 *    uploaded as the expense's photo. Backend already accepts ``.pdf``.
 */
const FULL_FRAME_QUALITY = 0.95;

const ExpenseCameraCapture = ({
  open, onClose, onCaptured, onExtracted,
  uploadEndpoint = "/expenses/upload-photo",
}) => {
  const videoRef    = useRef(null);
  const streamRef   = useRef(null);
  const ocrCanvasRef = useRef(null); // last cropped+enhanced canvas (for OCR)
  const [error,        setError]        = useState(null);
  const [snapshotUrl,  setSnapshotUrl]  = useState(null);  // preview image of CURRENT page
  const [currentPage,  setCurrentPage]  = useState(null);  // { dataUrl, w, h } of CURRENT page
  const [pages,        setPages]        = useState([]);    // multi-page stack [{dataUrl,w,h,thumbUrl}]
  // After every manual snap we DON'T crop immediately. We capture the
  // full frame, run the auto-detector to seed a starting box, and let
  // the user drag the 4 corners to confirm the receipt boundary
  // (CamScanner-style). Only on "تأكيد القص" does the real crop +
  // enhance + page push happen.
  const [cropEdit, setCropEdit] = useState(null);
  /*  cropEdit = {
        imgDataUrl: string,        // full uncropped frame
        vw: int, vh: int,          // natural pixel dims
        box: { x1, y1, x2, y2 },   // normalised [0..1] crop rectangle
      } | null
  */
  const [uploading,    setUploading]    = useState(false);
  const [processing,   setProcessing]   = useState(false);
  const [ready,        setReady]        = useState(false);
  const [ocrBusy,      setOcrBusy]      = useState(false);
  const [extracted,    setExtracted]    = useState(null); // {amount, vat_amount, ...}
  const [showExtract,  setShowExtract]  = useState(false);
  // Auto-shutter: progress from 0→100 as we accumulate stable detections.
  const [autoProgress, setAutoProgress] = useState(0);
  const [detected,     setDetected]     = useState(false);
  const snappedRef = useRef(false);  // guards against double-snap on auto+manual

  // Required helper used by both manual snap and auto-shutter detection.
  // Declared as a ref to avoid React hook ordering issues.
  const detectorRef = useRef(null);

  // ─── Camera lifecycle ─────────────────────────────────────────────
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setReady(false);
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: "environment" },
            width:      { ideal: 1920 },
            height:     { ideal: 1080 },
          },
        });
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        streamRef.current = stream;
        // Best-effort continuous autofocus
        const [track] = stream.getVideoTracks();
        if (track && track.applyConstraints) {
          const caps = track.getCapabilities ? track.getCapabilities() : {};
          if (caps.focusMode && caps.focusMode.includes("continuous")) {
            try { await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] }); } catch {/* ignore */}
          }
        }
        // Wait for the <video> element to mount (it renders only when
        // !error && !snapshotUrl — which is true at first paint, but a
        // microtask gap can leave the ref null on slower devices).
        let attempts = 0;
        while (!videoRef.current && attempts < 30) {
          await new Promise(r => requestAnimationFrame(r));
          attempts += 1;
        }
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          try {
            await videoRef.current.play();
          } catch {
            // Some browsers throw on autoplay; loadedmetadata then play
            await new Promise(r => {
              const v = videoRef.current;
              if (!v) return r();
              v.onloadedmetadata = () => { v.play().catch(() => {}); r(); };
            });
          }
          setReady(true);
        } else {
          setError("تعذّر تشغيل بث الكاميرا");
          stream.getTracks().forEach(t => t.stop());
        }
      } catch (e) {
        setError(
          "تعذّر الوصول إلى الكاميرا. تأكد من السماح للموقع باستخدام الكاميرا."
        );
      }
    })();
    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      if (snapshotUrl) URL.revokeObjectURL(snapshotUrl);
      // Revoke any thumbnail blobs we created for the page stack
      pages.forEach(p => { if (p.thumbUrl) URL.revokeObjectURL(p.thumbUrl); });
      setSnapshotUrl(null);
      setCurrentPage(null);
      setPages([]);
      setCropEdit(null);
      setError(null);
      setProcessing(false);
      setReady(false);
      setOcrBusy(false);
      setExtracted(null);
      setShowExtract(false);
      setAutoProgress(0);
      setDetected(false);
      snappedRef.current = false;
    };
  }, [open]);

  // Re-bind the live stream to the <video> element whenever it remounts.
  // The element is conditionally rendered behind `!snapshotUrl`, so going
  // from preview → "retake" / "صفحة أخرى" → live view creates a fresh
  // <video> node with no srcObject. Without this, the user sees a black
  // viewport after capturing a page and asking for another.
  useEffect(() => {
    if (snapshotUrl) return undefined;          // <video> not mounted now
    if (!streamRef.current) return undefined;   // stream not acquired yet
    const v = videoRef.current;
    if (!v) return undefined;
    if (v.srcObject !== streamRef.current) {
      v.srcObject = streamRef.current;
      v.play().catch(() => { /* autoplay race — harmless */ });
    }
    return undefined;
  }, [snapshotUrl]);


  // ─── Edge-detect: find the bounding box of the receipt ────────────
  // Strategy: receipts are typically lighter than the surrounding scene.
  // We binarise on luminance, then find leftmost/rightmost/top/bottom
  // light pixels with row/column light-coverage above a threshold to
  // ignore stray highlights. Falls back to a centered viewfinder crop
  // when detection is unreliable.
  const detectReceiptBounds = (imgData, w, h) => {
    const data = imgData.data;
    // Compute luminance histogram → adaptive threshold (Otsu-lite)
    const hist = new Uint32Array(256);
    for (let i = 0; i < data.length; i += 4) {
      const y = (data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000 | 0;
      hist[y]++;
    }
    let total = w * h, sum = 0;
    for (let t = 0; t < 256; t++) sum += t * hist[t];
    let sumB = 0, wB = 0, maxVar = 0, threshold = 128;
    for (let t = 0; t < 256; t++) {
      wB += hist[t];
      if (!wB) continue;
      const wF = total - wB;
      if (!wF) break;
      sumB += t * hist[t];
      const mB = sumB / wB;
      const mF = (sum - sumB) / wF;
      const v = wB * wF * (mB - mF) * (mB - mF);
      if (v > maxVar) { maxVar = v; threshold = t; }
    }
    // Lift threshold a touch so we focus on truly "white-paper" pixels
    threshold = Math.min(245, Math.max(threshold, 150));

    // Row / column light coverage
    const rowLight = new Uint32Array(h);
    const colLight = new Uint32Array(w);
    for (let y = 0; y < h; y++) {
      const row = y * w * 4;
      for (let x = 0; x < w; x++) {
        const i = row + x * 4;
        const lum = (data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000;
        if (lum >= threshold) {
          rowLight[y]++;
          colLight[x]++;
        }
      }
    }
    // Need at least 18% of the row/column to be light to count
    const minRowLight = Math.floor(w * 0.18);
    const minColLight = Math.floor(h * 0.18);
    let top = 0;    while (top < h    && rowLight[top]    < minRowLight) top++;
    let bottom = h - 1; while (bottom > top && rowLight[bottom] < minRowLight) bottom--;
    let left = 0;   while (left < w   && colLight[left]   < minColLight) left++;
    let right = w - 1;  while (right > left && colLight[right]  < minColLight) right--;

    const detW = right - left;
    const detH = bottom - top;
    // Sanity — fall back to viewfinder crop if detection collapsed
    if (detW < w * 0.25 || detH < h * 0.25) {
      const cropW = Math.floor(Math.min(w, h * 0.75) * 0.9);
      const cropH = Math.floor(cropW * 1.35);
      const sx = Math.floor((w - cropW) / 2);
      const sy = Math.floor((h - Math.min(cropH, h)) / 2);
      return { sx, sy, sw: cropW, sh: Math.min(cropH, h) };
    }
    // Add a small margin (2%) and clamp
    const mx = Math.floor(w * 0.015);
    const my = Math.floor(h * 0.015);
    const sx = Math.max(0, left - mx);
    const sy = Math.max(0, top - my);
    const sw = Math.min(w - sx, detW + 2 * mx);
    const sh = Math.min(h - sy, detH + 2 * my);
    return { sx, sy, sw, sh };
  };

  // ─── Document-scanner enhancement ─────────────────────────────────
  // Contrast stretch on luminance + mild sharpening.
  const enhance = (ctx, w, h) => {
    const img = ctx.getImageData(0, 0, w, h);
    const d = img.data;
    // Find 2nd/98th percentile of luminance for histogram stretch
    const hist = new Uint32Array(256);
    for (let i = 0; i < d.length; i += 4) {
      const y = (d[i] * 299 + d[i + 1] * 587 + d[i + 2] * 114) / 1000 | 0;
      hist[y]++;
    }
    const total = w * h;
    let lo = 0, hi = 255, acc = 0;
    for (let t = 0; t < 256; t++) { acc += hist[t]; if (acc >= total * 0.02) { lo = t; break; } }
    acc = 0;
    for (let t = 255; t >= 0; t--) { acc += hist[t]; if (acc >= total * 0.02) { hi = t; break; } }
    const range = Math.max(1, hi - lo);
    for (let i = 0; i < d.length; i += 4) {
      for (let c = 0; c < 3; c++) {
        const v = d[i + c];
        const s = ((v - lo) / range) * 255;
        d[i + c] = s < 0 ? 0 : s > 255 ? 255 : s;
      }
    }
    ctx.putImageData(img, 0, 0);
  };

  // ─── Capture → save full frame for manual cropping ────────────────
  // We no longer auto-crop the snapshot. The receipt edge-detector is
  // used only to *seed* the initial crop rectangle in the editor — the
  // user can then drag any of the 4 corners to confirm exactly what
  // gets included in the final PDF. This was the user's explicit
  // request: "حدّد الإيصال أولاً ثم التقاط الصورة".
  const snap = async () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) {
      toast.error("الكاميرا لم تجهز بعد");
      return;
    }
    setProcessing(true);
    try {
      const vw = v.videoWidth;
      const vh = v.videoHeight;
      // Capture full frame at native resolution
      const full = document.createElement("canvas");
      full.width = vw;
      full.height = vh;
      const fctx = full.getContext("2d");
      fctx.drawImage(v, 0, 0, vw, vh);

      // Seed the crop rectangle with the auto-detector. Falls back to a
      // generous centered box if detection collapses.
      let initial = { sx: 0, sy: 0, sw: vw, sh: vh };
      try {
        const imgData = fctx.getImageData(0, 0, vw, vh);
        initial = detectReceiptBounds(imgData, vw, vh);
      } catch {
        // detector failed — keep full-frame default
      }
      const x1 = Math.max(0, initial.sx / vw);
      const y1 = Math.max(0, initial.sy / vh);
      const x2 = Math.min(1, (initial.sx + initial.sw) / vw);
      const y2 = Math.min(1, (initial.sy + initial.sh) / vh);

      const imgDataUrl = full.toDataURL("image/jpeg", FULL_FRAME_QUALITY);
      setCropEdit({
        imgDataUrl,
        vw,
        vh,
        box: { x1, y1, x2, y2 },
      });
    } catch (e) {
      toast.error("فشل التقاط الصورة");
    } finally {
      setProcessing(false);
    }
  };

  // ─── Apply the user-confirmed crop ────────────────────────────────
  // Runs when the user clicks "تأكيد القص" in the editor. Performs the
  // actual pixel crop, enhances the result for legibility, runs OCR on
  // the first page only, and pushes the result as the current page.
  const confirmCrop = async () => {
    if (!cropEdit) return;
    setProcessing(true);
    try {
      const { imgDataUrl, vw, vh, box } = cropEdit;
      const img = new Image();
      img.src = imgDataUrl;
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });
      const sx = Math.round(box.x1 * vw);
      const sy = Math.round(box.y1 * vh);
      const sw = Math.max(1, Math.round((box.x2 - box.x1) * vw));
      const sh = Math.max(1, Math.round((box.y2 - box.y1) * vh));
      const out = document.createElement("canvas");
      out.width  = sw;
      out.height = sh;
      const octx = out.getContext("2d");
      octx.imageSmoothingQuality = "high";
      octx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
      // Document-scanner enhancement
      enhance(octx, sw, sh);
      ocrCanvasRef.current = out;

      const jpegBlob = await new Promise(resolve =>
        out.toBlob(resolve, "image/jpeg", FULL_FRAME_QUALITY));
      if (!jpegBlob) throw new Error("toBlob_failed");
      const previewUrl = URL.createObjectURL(jpegBlob);
      const dataUrl = out.toDataURL("image/jpeg", FULL_FRAME_QUALITY);

      setSnapshotUrl(previewUrl);
      setCurrentPage({ dataUrl, w: sw, h: sh, thumbUrl: previewUrl });
      setCropEdit(null);

      // Fire OCR on the first page only — back-of-receipt pages don't
      // carry the totals row we extract.
      if (pages.length === 0) {
        setOcrBusy(true);
        ocrReceipt(out)
          .then(({ fields }) => {
            const anyHit = Object.values(fields || {}).some(v => v !== null && v !== "");
            if (anyHit) {
              setExtracted(fields);
              setShowExtract(true);
            }
          })
          .catch(() => { /* silent */ })
          .finally(() => setOcrBusy(false));
      }
    } catch (e) {
      toast.error("فشل قص الإيصال");
    } finally {
      setProcessing(false);
    }
  };

  const cancelCrop = () => {
    setCropEdit(null);
    setAutoProgress(0);
    setDetected(false);
    snappedRef.current = false;
  };

  const retake = () => {
    if (snapshotUrl) URL.revokeObjectURL(snapshotUrl);
    setSnapshotUrl(null);
    setCurrentPage(null);
    setAutoProgress(0);
    setDetected(false);
    snappedRef.current = false;
  };

  // Push the current snapshot into the multi-page stack and re-arm the
  // camera so the user can scan another page. Triggered by "صفحة أخرى".
  const addAnotherPage = () => {
    if (!currentPage) return;
    setPages(prev => [...prev, currentPage]);
    setSnapshotUrl(null);
    setCurrentPage(null);
    setAutoProgress(0);
    setDetected(false);
    snappedRef.current = false;
    toast.success(`تمت إضافة الصفحة (${pages.length + 1})`);
  };

  // Remove a page from the stack (e.g. user wants to drop an unclear shot)
  const removePage = (idx) => {
    setPages(prev => {
      const p = prev[idx];
      if (p?.thumbUrl) URL.revokeObjectURL(p.thumbUrl);
      return prev.filter((_, i) => i !== idx);
    });
  };

  // ─── Receipt-edge live indicator (no auto-shutter) ────────────────
  // The user explicitly asked for manual control: "حدّد الإيصال أولاً
  // ثم التقاط الصورة". So we keep the live edge-detector running purely
  // to colour the on-screen guide rectangle green when a receipt-shape
  // is in view — but we DO NOT auto-trigger snap(). The user always
  // makes the capture decision themselves, and then gets the corner-
  // adjustable crop editor.
  useEffect(() => {
    if (!open || !ready || processing || snapshotUrl || cropEdit) {
      return undefined;
    }
    let cancelled = false;
    const analyzeOne = () => {
      if (cancelled) return;
      const v = videoRef.current;
      if (!v || !v.videoWidth) return;
      const W = 320;
      const H = Math.max(120, Math.round(W * v.videoHeight / v.videoWidth));
      const c = document.createElement("canvas");
      c.width = W; c.height = H;
      const ctx = c.getContext("2d");
      try {
        ctx.drawImage(v, 0, 0, W, H);
        const imgData = ctx.getImageData(0, 0, W, H);
        const b = detectReceiptBounds(imgData, W, H);
        const areaFrac = (b.sw * b.sh) / (W * H);
        const good = areaFrac > 0.30 && b.sw > W * 0.35 && b.sh > H * 0.35;
        setDetected(good);
        setAutoProgress(good ? 100 : 0);
      } catch { /* ignore */ }
    };
    const id = setInterval(analyzeOne, 600);
    analyzeOne();
    return () => { cancelled = true; clearInterval(id); };
  }, [open, ready, processing, snapshotUrl, cropEdit]);

  // Build the final multi-page PDF from the page stack (+ current page,
  // if not yet pushed) and upload it. One file, even when many pages.
  const acceptAndUpload = async () => {
    // Collect all pages: existing stack + the one currently on screen
    const allPages = [...pages];
    if (currentPage) allPages.push(currentPage);
    if (allPages.length === 0) return;

    setUploading(true);
    try {
      // First page sets the document orientation/format
      const first = allPages[0];
      const pdf = new jsPDF({
        orientation: first.h >= first.w ? "portrait" : "landscape",
        unit: "px",
        format: [first.w, first.h],
        hotfixes: ["px_scaling"],
      });
      pdf.addImage(first.dataUrl, "JPEG", 0, 0, first.w, first.h, "", "FAST");
      // Subsequent pages — each can have its own size/orientation
      for (let i = 1; i < allPages.length; i++) {
        const p = allPages[i];
        pdf.addPage([p.w, p.h], p.h >= p.w ? "portrait" : "landscape");
        pdf.addImage(p.dataUrl, "JPEG", 0, 0, p.w, p.h, "", "FAST");
      }
      const blob = pdf.output("blob");

      const fd = new FormData();
      const filename = `expense_receipt_${Date.now()}.pdf`;
      fd.append("file", blob, filename);
      const r = await axiosInstance.post(uploadEndpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const photoUrl = r.data.photo_url || r.data.url;
      if (!photoUrl) throw new Error("no photo_url returned");
      toast.success(
        allPages.length > 1
          ? `تم رفع PDF (${allPages.length} صفحات)`
          : "تم رفع إيصال PDF"
      );
      onCaptured?.(photoUrl);
      onClose?.();
    } catch {
      toast.error("فشل رفع الإيصال");
    } finally {
      setUploading(false);
    }
  };

  const applyExtracted = () => {
    onExtracted?.(extracted || {});
    setShowExtract(false);
    toast.success("تم تعبئة الحقول من الإيصال");
  };

  const skipExtracted = () => {
    setShowExtract(false);
  };

  if (!open) return null;

  return createPortal(
    <div
      data-testid="expense-camera-modal"
      className="fixed inset-0 z-[9999] bg-black flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white">
        <div className="flex items-center gap-2 text-sm font-bold">
          <ScanLine className="w-4 h-4" />
          ماسح الإيصالات
          {pages.length > 0 && (
            <span
              data-testid="expense-camera-page-count"
              className="ml-1 px-2 py-0.5 rounded-full bg-emerald-500 text-[10px] font-bold"
            >
              {pages.length + (currentPage ? 1 : 0)} صفحة
            </span>
          )}
        </div>
        <button
          data-testid="expense-camera-close"
          onClick={onClose}
          className="p-1.5 rounded hover:bg-slate-700"
          aria-label="إغلاق"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Live frame / preview — fills remaining space */}
      <div className="relative flex-1 bg-black overflow-hidden">
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center text-white">
            <AlertTriangle className="w-10 h-10 text-amber-400" />
            <p className="text-sm leading-relaxed">{error}</p>
          </div>
        )}

        {!error && !snapshotUrl && !cropEdit && (
          <>
            <video
              ref={videoRef}
              playsInline
              muted
              className="w-full h-full object-contain"
              data-testid="expense-camera-video"
            />
            {/* Soft document-shaped guidance overlay — colour shifts as
                the auto-shutter locks onto a receipt. */}
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div
                className={`w-[78%] h-[82%] max-w-[420px] border-2 rounded-md transition-colors duration-200 ${
                  detected
                    ? "border-emerald-400 shadow-[0_0_0_4px_rgba(16,185,129,0.25)]"
                    : "border-dashed border-emerald-300/70"
                }`}
              />
            </div>
            <div
              className={`absolute bottom-3 left-1/2 -translate-x-1/2 text-[11px] text-white px-3 py-1 rounded-full font-semibold transition-colors ${
                detected ? "bg-emerald-500" : "bg-emerald-700/90"
              }`}
              data-testid="expense-camera-hint"
            >
              {detected
                ? "✓ الإيصال داخل الإطار — اضغط التقاط ثم اضبط الحواف"
                : "أطّر الإيصال داخل المستطيل ثم اضغط التقاط"}
            </div>
            {/* Auto-shutter progress bar at top */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-black/30">
              <div
                className="h-full bg-emerald-400 transition-all duration-200"
                style={{ width: `${autoProgress}%` }}
              />
            </div>
            {processing && (
              <div className="absolute inset-0 bg-black/70 flex items-center justify-center text-white text-sm font-semibold gap-2">
                <RefreshCw className="w-5 h-5 animate-spin" /> جارٍ معالجة الإيصال…
              </div>
            )}
          </>
        )}

        {/* Crop editor — shown right after the user snaps. Image is
            presented at full resolution with a draggable 4-corner
            rectangle, seeded by the auto-detector. User confirms the
            boundary before any pixels are dropped. */}
        {cropEdit && (
          <CropEditor
            cropEdit={cropEdit}
            onBoxChange={(box) => setCropEdit(prev => prev ? { ...prev, box } : prev)}
            processing={processing}
          />
        )}

        {snapshotUrl && (
          <img
            src={snapshotUrl}
            alt="receipt preview"
            data-testid="expense-camera-preview"
            className="w-full h-full object-contain bg-slate-800"
          />
        )}

        {snapshotUrl && ocrBusy && !showExtract && (
          <div
            data-testid="expense-camera-ocr-busy"
            className="absolute top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-600/90 text-white text-xs font-semibold shadow-lg"
          >
            <Sparkles className="w-4 h-4 animate-pulse" />
            جارٍ قراءة الإيصال…
          </div>
        )}

        {showExtract && extracted && (
          <ExtractPreview
            data={extracted}
            onApply={applyExtracted}
            onSkip={skipExtracted}
          />
        )}
      </div>

      {/* Captured-pages thumbnail strip — visible whenever we have at
          least one stacked page. Lets the user drop a bad shot before
          building the final PDF. */}
      {pages.length > 0 && (
        <div
          data-testid="expense-camera-page-strip"
          className="flex items-center gap-2 overflow-x-auto px-3 py-2 bg-slate-800/95 border-t border-slate-700"
        >
          {pages.map((p, i) => (
            <div
              key={i}
              className="relative shrink-0 w-14 h-14 rounded border border-slate-600 overflow-hidden bg-slate-900"
              data-testid={`expense-camera-page-thumb-${i}`}
            >
              <img
                src={p.thumbUrl}
                alt={`page-${i + 1}`}
                className="w-full h-full object-cover opacity-90"
              />
              <span className="absolute top-0 left-0 bg-black/70 text-white text-[9px] px-1 rounded-br">
                {i + 1}
              </span>
              <button
                onClick={() => removePage(i)}
                disabled={uploading}
                className="absolute top-0 right-0 bg-red-600/90 hover:bg-red-700 text-white p-0.5 rounded-bl"
                data-testid={`expense-camera-page-remove-${i}`}
                aria-label="حذف الصفحة"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Footer controls */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 bg-slate-900">
        {cropEdit ? (
          // ── Crop-confirm phase ──────────────────────────────────────
          <>
            <button
              data-testid="expense-camera-crop-cancel"
              onClick={cancelCrop}
              disabled={processing}
              className="inline-flex items-center gap-1 px-3 py-2 text-xs text-amber-400 hover:bg-slate-800 rounded-lg"
            >
              <RefreshCw className="w-4 h-4" /> إعادة الالتقاط
            </button>
            <span className="text-[11px] text-slate-300 text-center px-2 hidden sm:block">
              اسحب الزوايا الأربع لتحديد حدود الإيصال بدقة
            </span>
            <button
              data-testid="expense-camera-crop-confirm"
              onClick={confirmCrop}
              disabled={processing}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-700 text-white font-bold rounded-full"
            >
              {processing ? (
                <><RefreshCw className="w-4 h-4 animate-spin" /> جارٍ القص…</>
              ) : (
                <><Crop className="w-5 h-5" /> تأكيد القص</>
              )}
            </button>
          </>
        ) : !snapshotUrl ? (
          <>
            <button
              onClick={onClose}
              className="px-3 py-2 text-xs text-slate-300 hover:bg-slate-800 rounded-lg"
            >
              إلغاء
            </button>
            <button
              data-testid="expense-camera-snap"
              onClick={snap}
              disabled={!!error || !ready || processing}
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-700 disabled:text-slate-400 text-white font-bold rounded-full"
            >
              <Camera className="w-5 h-5" /> التقاط يدوي
            </button>
            {/* When the user already stacked pages, expose a quick "حفظ
                كـ PDF" so they don't HAVE to scan another page just to
                build the document. */}
            {pages.length > 0 ? (
              <button
                data-testid="expense-camera-finalize"
                onClick={acceptAndUpload}
                disabled={uploading}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-violet-600 hover:bg-violet-700 disabled:bg-slate-700 text-white text-xs font-bold rounded-lg"
              >
                {uploading ? (
                  <><RefreshCw className="w-4 h-4 animate-spin" /> رفع…</>
                ) : (
                  <><Check className="w-4 h-4" /> حفظ ({pages.length})</>
                )}
              </button>
            ) : (
              <span className="w-12" />
            )}
          </>
        ) : (
          <>
            <button
              data-testid="expense-camera-retake"
              onClick={retake}
              disabled={uploading}
              className="inline-flex items-center gap-1 px-3 py-2 text-xs text-amber-400 hover:bg-slate-800 rounded-lg"
            >
              <RefreshCw className="w-4 h-4" /> إعادة الالتقاط
            </button>
            <button
              data-testid="expense-camera-add-page"
              onClick={addAnotherPage}
              disabled={uploading || !currentPage}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs text-emerald-300 hover:bg-slate-800 border border-emerald-500/40 rounded-lg"
            >
              <FilePlus className="w-4 h-4" /> صفحة أخرى
            </button>
            <button
              data-testid="expense-camera-accept"
              onClick={acceptAndUpload}
              disabled={uploading}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-700 text-white font-bold rounded-full"
            >
              {uploading ? (
                <><RefreshCw className="w-4 h-4 animate-spin" /> جارٍ الرفع…</>
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  حفظ كـ PDF{pages.length > 0 ? ` (${pages.length + 1})` : ''}
                </>
              )}
            </button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ExpenseCameraCapture;

// ─── Adjustable crop editor (CamScanner-style) ──────────────────────
// Renders the full captured frame with a draggable rectangle. The
// rectangle's four corners can be dragged to reshape the box; tapping
// inside the box drags the whole thing. All coordinates are stored
// normalised [0..1] so the editor doesn't have to know the actual
// canvas size — works on phone, tablet, desktop equally.
const CropEditor = ({ cropEdit, onBoxChange, processing }) => {
  const wrapRef = useRef(null);
  const imgRef  = useRef(null);
  const [imgRect, setImgRect] = useState(null); // {x, y, w, h} of the rendered image inside the wrapper
  const dragRef = useRef(null);                 // { mode: 'tl'|'tr'|'bl'|'br'|'move', startBox, startPt }

  // Re-measure the visible image rect whenever the wrapper resizes or
  // the image loads. We need this because <img object-contain> letter-
  // boxes the image, so pointer coords have to be offset by the bands.
  const measure = useCallback(() => {
    const wrap = wrapRef.current;
    const img  = imgRef.current;
    if (!wrap || !img || !img.naturalWidth) return;
    const ww = wrap.clientWidth;
    const wh = wrap.clientHeight;
    const ar = img.naturalWidth / img.naturalHeight;
    const wrapAr = ww / wh;
    let w, h, x, y;
    if (ar > wrapAr) {
      // image is wider than wrapper → letterbox top/bottom
      w = ww;
      h = ww / ar;
      x = 0;
      y = (wh - h) / 2;
    } else {
      h = wh;
      w = wh * ar;
      y = 0;
      x = (ww - w) / 2;
    }
    setImgRect({ x, y, w, h });
  }, []);

  useEffect(() => {
    measure();
    const ro = new ResizeObserver(measure);
    if (wrapRef.current) ro.observe(wrapRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  const onImgLoad = () => measure();

  // ─ Drag math ───────────────────────────────────────────────────────
  // Convert a pointer event into normalised image-space [0..1].
  const toImgSpace = (e) => {
    if (!imgRect || !wrapRef.current) return null;
    const wrapBox = wrapRef.current.getBoundingClientRect();
    const px = e.clientX - wrapBox.left - imgRect.x;
    const py = e.clientY - wrapBox.top  - imgRect.y;
    return {
      x: Math.min(1, Math.max(0, px / imgRect.w)),
      y: Math.min(1, Math.max(0, py / imgRect.h)),
    };
  };

  const onPointerDown = (mode) => (e) => {
    if (processing) return;
    e.preventDefault();
    e.target.setPointerCapture?.(e.pointerId);
    const pt = toImgSpace(e);
    if (!pt) return;
    dragRef.current = { mode, startBox: { ...cropEdit.box }, startPt: pt };
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const pt = toImgSpace(e);
    if (!pt) return;
    const { mode, startBox, startPt } = d;
    const MIN = 0.08; // minimum box size as fraction of image
    let { x1, y1, x2, y2 } = startBox;
    if (mode === "move") {
      const dx = pt.x - startPt.x;
      const dy = pt.y - startPt.y;
      const w = x2 - x1;
      const h = y2 - y1;
      x1 = Math.min(1 - w, Math.max(0, x1 + dx));
      y1 = Math.min(1 - h, Math.max(0, y1 + dy));
      x2 = x1 + w;
      y2 = y1 + h;
    } else {
      if (mode.includes("l")) x1 = Math.min(x2 - MIN, Math.max(0, pt.x));
      if (mode.includes("r")) x2 = Math.max(x1 + MIN, Math.min(1, pt.x));
      if (mode.includes("t")) y1 = Math.min(y2 - MIN, Math.max(0, pt.y));
      if (mode.includes("b")) y2 = Math.max(y1 + MIN, Math.min(1, pt.y));
    }
    onBoxChange({ x1, y1, x2, y2 });
  };

  const onPointerUp = () => { dragRef.current = null; };

  const { box } = cropEdit;
  // Pixel position of the box overlay inside the wrapper
  const overlay = imgRect
    ? {
        left:   imgRect.x + box.x1 * imgRect.w,
        top:    imgRect.y + box.y1 * imgRect.h,
        width:  (box.x2 - box.x1) * imgRect.w,
        height: (box.y2 - box.y1) * imgRect.h,
      }
    : null;

  return (
    <div
      ref={wrapRef}
      data-testid="expense-camera-crop-editor"
      className="absolute inset-0 bg-slate-900 select-none touch-none"
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <img
        ref={imgRef}
        src={cropEdit.imgDataUrl}
        alt="captured"
        onLoad={onImgLoad}
        className="absolute inset-0 w-full h-full object-contain"
        draggable={false}
      />
      {overlay && (
        <>
          {/* Dim outside the box — four bands so we don't fight pointer events */}
          <div className="absolute inset-0 pointer-events-none" style={{
            background:
              `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55))`,
            clipPath: `polygon(
              0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%,
              ${overlay.left}px ${overlay.top}px,
              ${overlay.left}px ${overlay.top + overlay.height}px,
              ${overlay.left + overlay.width}px ${overlay.top + overlay.height}px,
              ${overlay.left + overlay.width}px ${overlay.top}px,
              ${overlay.left}px ${overlay.top}px
            )`,
          }} />
          {/* Box outline (movable) */}
          <div
            data-testid="expense-camera-crop-box"
            className="absolute border-2 border-emerald-400 cursor-move"
            style={{
              left: overlay.left,
              top: overlay.top,
              width: overlay.width,
              height: overlay.height,
              boxShadow: "0 0 0 1px rgba(0,0,0,0.4) inset",
            }}
            onPointerDown={onPointerDown("move")}
          />
          {/* Four corner handles */}
          {[
            { id: "tl", left: overlay.left,                  top: overlay.top                  },
            { id: "tr", left: overlay.left + overlay.width,  top: overlay.top                  },
            { id: "bl", left: overlay.left,                  top: overlay.top + overlay.height },
            { id: "br", left: overlay.left + overlay.width,  top: overlay.top + overlay.height },
          ].map(h => (
            <div
              key={h.id}
              data-testid={`expense-camera-crop-handle-${h.id}`}
              className="absolute w-7 h-7 rounded-full bg-emerald-500 border-2 border-white shadow-lg touch-none"
              style={{
                left: h.left - 14,
                top:  h.top  - 14,
                cursor: { tl: "nwse-resize", br: "nwse-resize", tr: "nesw-resize", bl: "nesw-resize" }[h.id],
              }}
              onPointerDown={onPointerDown(h.id)}
            />
          ))}
        </>
      )}
      <div className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-emerald-600/90 text-white text-[11px] font-semibold shadow-lg">
        اسحب الزوايا الأربع لتحديد الإيصال
      </div>
    </div>
  );
};


// ─── Extracted-fields preview (OCR confirmation modal) ───────────────
const FIELD_LABELS = {
  vendor:       "المورّد",
  invoice_no:   "رقم الفاتورة",
  expense_date: "التاريخ",
  amount:       "المبلغ (قبل الضريبة)",
  vat_amount:   "ضريبة القيمة المضافة",
  description:  "الوصف",
};

const ExtractPreview = ({ data, onApply, onSkip }) => {
  const entries = Object.entries(FIELD_LABELS)
    .map(([k, label]) => [k, label, data?.[k]])
    .filter(([, , v]) => v !== null && v !== undefined && v !== "");
  return (
    <div
      data-testid="expense-ocr-preview"
      className="absolute inset-0 bg-black/85 flex items-center justify-center p-4"
    >
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-4 py-3 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          <span className="text-sm font-bold">تم استخراج البيانات من الإيصال</span>
        </div>
        <div className="p-4 space-y-2">
          {entries.length === 0 ? (
            <p className="text-xs text-slate-600 text-center py-3">
              لم نتمكن من التعرّف على بيانات واضحة. يمكنك المتابعة وإدخالها يدوياً.
            </p>
          ) : entries.map(([k, label, val]) => (
            <div
              key={k}
              className="flex items-center justify-between gap-3 px-2 py-1.5 rounded-md hover:bg-slate-50"
              data-testid={`expense-ocr-field-${k}`}
            >
              <span className="text-[11px] text-slate-500 font-medium">{label}</span>
              <span className="text-sm text-slate-800 font-semibold text-left ltr direction-ltr"
                    dir="ltr">{String(val)}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-end gap-2 px-3 py-3 bg-slate-50 border-t border-slate-200">
          <button
            data-testid="expense-ocr-skip"
            onClick={onSkip}
            className="px-3 py-2 text-xs text-slate-600 hover:bg-slate-200 rounded-lg"
          >
            تخطّي
          </button>
          <button
            data-testid="expense-ocr-apply"
            onClick={onApply}
            disabled={entries.length === 0}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300 rounded-lg"
          >
            <Check className="w-3.5 h-3.5" />
            تعبئة الحقول
          </button>
        </div>
      </div>
    </div>
  );
};
